import { Building2, Target } from 'lucide-react'
import type { ClienteAgradaDetail } from '@/services/clientesService'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format-date'
import {
  formatClienteAgradaStatus,
  formatClienteCnpj,
  getClienteInitials,
} from './clienteAgradaDetailUtils'

type Props = {
  detail: ClienteAgradaDetail
}

const METRIC_ACCENTS = {
  deals: '#987de8',
  projetos: '#3b82f6',
  servicos: '#6366f1',
  chamados: '#f59e0b',
} as const

function HeaderMetric({
  label,
  value,
  accent,
}: {
  label: string
  value: number
  accent: string
}) {
  return (
    <div className='rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-2.5 text-center'>
      <p
        className='text-[10px] font-semibold uppercase tracking-wider text-text-muted'
        style={{ color: accent }}
      >
        {label}
      </p>
      <p className='mt-0.5 text-lg font-bold tabular-nums text-text-primary'>{value}</p>
    </div>
  )
}

export function ClienteAgradaDetailHeader({ detail }: Props) {
  const displayName = detail.nomeFantasia?.trim() || detail.nome
  const razao = detail.razaoSocial?.trim()
  const subtitleParts = [
    razao && razao !== displayName ? razao : null,
    detail.cnpj ? formatClienteCnpj(detail.cnpj) : null,
  ].filter(Boolean)

  const primaryClienteAtivo = detail.clienteAtivos[0]
  const statusLabel = primaryClienteAtivo
    ? formatClienteAgradaStatus(detail.status === 'CLIENTE_ATIVO' ? detail.status : 'CLIENTE_ATIVO')
    : formatClienteAgradaStatus(detail.status)
  const isActive = detail.status === 'CLIENTE_ATIVO' || (detail.clienteAtivos?.length ?? 0) > 0

  return (
    <section className='glass-card relative mb-5 rounded-xl p-5 md:p-6'>
      <div
        className='pointer-events-none absolute inset-x-0 top-0 h-[2px] rounded-t-xl'
        style={{
          backgroundImage:
            'linear-gradient(to right, transparent, rgba(239,68,68,0.45), transparent)',
        }}
      />

      <div className='flex flex-col gap-4'>
        <div className='flex items-start gap-4'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-base font-bold text-accent md:h-14 md:w-14 md:text-lg'>
            {getClienteInitials(displayName)}
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <h1 className='text-lg font-bold text-text-primary md:text-xl'>{displayName}</h1>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                  isActive
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : 'bg-amber-500/15 text-amber-400',
                )}
              >
                {statusLabel}
              </span>
            </div>
            {subtitleParts.length > 0 && (
              <p className='mt-1 text-sm text-text-muted'>{subtitleParts.join(' · ')}</p>
            )}
            <p className='mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted'>
              <span className='inline-flex items-center gap-1'>
                <Building2 size={12} aria-hidden />
                Cadastro {formatDate(detail.createdAt)}
              </span>
              <span>Atualizado {formatDate(detail.updatedAt)}</span>
            </p>
            {primaryClienteAtivo && (
              <p className='mt-2 inline-flex max-w-full flex-wrap items-center gap-1.5 text-sm text-text-primary'>
                <Target size={13} className='shrink-0 text-accent' aria-hidden />
                <span className='font-medium'>{primaryClienteAtivo.deal?.nome ?? 'Deal vinculado'}</span>
                <span className='text-text-muted'>· Closer {primaryClienteAtivo.closer.name}</span>
              </p>
            )}
          </div>
        </div>

        <div className='grid grid-cols-2 gap-2 border-t border-white/[0.06] pt-4 sm:grid-cols-4'>
          <HeaderMetric label='Deals' value={detail.deals?.length ?? 0} accent={METRIC_ACCENTS.deals} />
          <HeaderMetric
            label='Projetos'
            value={detail.financeProjects?.length ?? 0}
            accent={METRIC_ACCENTS.projetos}
          />
          <HeaderMetric
            label='Serviços'
            value={detail.servicos?.length ?? 0}
            accent={METRIC_ACCENTS.servicos}
          />
          <HeaderMetric
            label='Chamados'
            value={detail.chamados?.length ?? 0}
            accent={METRIC_ACCENTS.chamados}
          />
        </div>
      </div>
    </section>
  )
}
